import type { HomeRecentAgentSessionAssociationsQuery$variables } from './__generated__/HomeRecentAgentSessionAssociationsQuery.graphql.ts';
import type { HomeRecentAgentSessionsQuery$variables } from './__generated__/HomeRecentAgentSessionsQuery.graphql.ts';

/**
 * How many of the fetched sessions are actually rendered. Anything the backend returns beyond this is overscan.
 */
export const RECENT_AGENT_SESSIONS_DISPLAY_COUNT = 2;

/**
 * The backend applies `first` before `aqlQuery`, so session queries have to overscan.
 */
export const AGENTS_WAITING_FOR_YOUR_INPUT_PAGE_SIZE = 25;

const AGENTS_WAITING_FOR_YOUR_INPUT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const HOUR_MS = 60 * 60 * 1000;

/**
 * The section "Agents waiting for your input" shows only the sessions whose state changed in the last 7 days.
 *
 * The bound is floored to the hour so that the SSR preload and the hydrating client compute the same query
 * variables (and hit the same Relay store records) instead of each computing its own millisecond.
 */
export const getAgentsWaitingForYourInputSince = (now: number = Date.now()): number =>
	Math.floor((now - AGENTS_WAITING_FOR_YOUR_INPUT_MAX_AGE_MS) / HOUR_MS) * HOUR_MS;

const escapeAqlValue = (value: string): string => value.replace(/\\/g, '\\\\').replace(/'/g, "\\'");

const INVOCATION_TYPE_FILTER =
	"invocationType = 'CONFLUENCE_COMMENT_MENTION'" +
	" OR invocationType = 'CONFLUENCE_CHAT_EDIT'" +
	" OR invocationType = 'CONFLUENCE_INLINE_EDIT'" +
	" OR invocationType = 'CONFLUENCE_CWR_EDIT'" +
	" OR invocationType = 'CONFLUENCE_CWR_CREATE'" +
	" OR invocationType = 'CONFLUENCE_CWR_EXISTING'" +
	" OR invocationType = 'CONFLUENCE_REMIX'" +
	" OR invocationType = 'CONFLUENCE_REMIX_EDIT'" +
	" OR invocationType = 'CONFLUENCE_CHAT'";

/**
 * Set by the backend on Content Review sessions whose suggestions are ready to review. These sessions are
 * otherwise indistinguishable from other `CONFLUENCE_CHAT_EDIT` sessions in the `ARTEFACT_READY` state.
 */
export const CONTENT_REVIEW_READY_STATE_REASON = 'CONTENT_REVIEW_READY';
export const CONFLUENCE_CREATION_READY_STATE_REASON = 'CONFLUENCE_CREATION_READY';

export const getRecentAgentSessionsQueryVariables = ({
	userId,
	rovoActivationId,
	first = AGENTS_WAITING_FOR_YOUR_INPUT_PAGE_SIZE,
	since,
}: {
	userId: string;
	rovoActivationId: string;
	first?: number;
	/** Only sessions whose `lastStateChangeAt` is at or after this epoch millisecond. */
	since?: number;
}): HomeRecentAgentSessionsQuery$variables => {
	const escapedUserId = escapeAqlValue(userId);
	return {
		aqlQuery:
			`authorAccountId = '${escapedUserId}'${getSinceFilter(since)}` +
			" AND (((state = 'INPUT_REQUIRED' OR state = 'AUTH_REQUIRED')" +
			` AND (${INVOCATION_TYPE_FILTER}))` +
			" OR (state = 'INPUT_REQUIRED' AND stateReason = 'CONFLUENCE_EDIT_CONFIRMATION_REQUIRED')" +
			" OR (state = 'ARTEFACT_READY' AND invocationType = 'CONFLUENCE_CWR_CREATE')" +
			` OR (state = 'ARTEFACT_READY' AND stateReason = '${CONFLUENCE_CREATION_READY_STATE_REASON}')` +
			` OR (state = 'ARTEFACT_READY' AND stateReason = '${CONTENT_REVIEW_READY_STATE_REASON}'))`,
		shardingContext: `ari:cloud:rovo::workspace/${rovoActivationId}`,
		first,
	};
};

/**
 * Associations are keyed by (session, entity), so one session can hold several. This bounds the
 * scoped association query generously enough that no requested session is silently truncated away.
 */
const MAX_ASSOCIATIONS_PER_SESSION = 5;

export const getRecentAgentSessionAssociationsQueryVariables = ({
	associatedEntities,
	first = associatedEntities.length * MAX_ASSOCIATIONS_PER_SESSION,
	rovoActivationId,
	since,
	userId,
}: {
	associatedEntities: ReadonlyArray<{ entityId: string; sessionId: string }>;
	/**
	 * The backend applies `first` before `aqlQuery` here too, so the default does not actually bound the
	 * associations of the requested sessions. Paginate with `since` instead.
	 */
	first?: number;
	rovoActivationId: string;
	/** Only associations whose `lastStateChangeAt` (that of their session) is at or after this epoch millisecond. */
	since?: number;
	userId: string;
}): HomeRecentAgentSessionAssociationsQuery$variables => {
	const sessionEntityFilter = associatedEntities
		.map(
			({ entityId, sessionId }) =>
				`(entityId = '${escapeAqlValue(entityId)}' AND agentSessionId = '${escapeAqlValue(
					sessionId,
				)}')`,
		)
		.join(' OR ');
	return {
		aqlQuery:
			`userAccountId = '${escapeAqlValue(userId)}'` +
			` AND deleted = false${getSinceFilter(since)}` +
			` AND (${sessionEntityFilter})`,
		first,
		shardingContext: `ari:cloud:rovo::workspace/${rovoActivationId}`,
	};
};

/**
 * The backend bounds which part of its index it reads only with top-level AND terms, so `lastStateChangeAt`
 * MUST NOT be nested inside parentheses. Everything else (e.g. `state`) is applied to each page after `first`.
 */
const getSinceFilter = (since: number | undefined): string =>
	since === undefined ? '' : ` AND lastStateChangeAt >= ${since}`;
